<template>
  <div class="page-wrapper">
    <!-- Header -->
    <div class="page-header mb-7">
      <div class="header-accent"></div>
      <div class="header-content d-flex align-center justify-space-between">
        <div class="d-flex align-center" style="gap: 14px">
          <div class="header-icon-wrap">
            <v-icon color="#00A63E" size="22">mdi-account-group-outline</v-icon>
          </div>
          <div>
            <div class="page-title">Users Management</div>
            <div class="page-subtitle">Manage users details</div>
          </div>
        </div>

        <div class="d-flex align-center" style="gap: 10px">
          <v-text-field
            v-model="search"
            placeholder="Search users..."
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            class="custom-search"
            style="width: 220px"
          />
          <v-btn
            class="add-btn"
            prepend-icon="mdi-plus"
            rounded="lg"
            @click="addnew"
          >
            Add New
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Filter Card -->
    <v-card flat class="filter-card pa-5 rounded-2xl mb-5">
      <div class="d-flex align-center flex-wrap" style="gap: 16px">
        <div class="filter-label-wrap d-flex align-center" style="gap: 6px">
          <v-icon color="#9ca3af" size="16">mdi-filter-variant</v-icon>
          <span class="filter-label">Filters</span>
        </div>
        <div class="filter-divider"></div>
        <div class="d-flex align-center" style="gap: 10px">
          <span class="filter-field-label">User Type</span>
          <div style="width: 210px">
            <v-autocomplete
              density="compact"
              v-model="type"
              item-value="id"
              item-title="description"
              @update:modelValue="filterTable"
              :color="$vuetify.theme.themes.light.inputColor"
              variant="outlined"
              hide-details
              :items="usertypeList"
              class="custom-select"
            />
          </div>
        </div>
      </div>
    </v-card>

    <!-- Table Card -->
    <v-card flat class="table-card rounded-2xl">
      <!-- Table topbar -->
      <div
        class="table-topbar px-5 py-4 d-flex align-center justify-space-between"
      >
        <div class="d-flex align-center" style="gap: 8px">
          <span class="table-title">All Users</span>
          <span v-if="getTableData.length > 0" class="count-badge">{{
            getTableData.length
          }}</span>
        </div>
      </div>

      <v-divider />

      <v-data-table
        :headers="headers"
        :items="getTableData"
        :search="search"
        :loading="loading"
        hover
        class="users-table"
      >
        <!-- Loading -->
        <template #loading>
          <div class="text-center py-10">
            <v-progress-circular indeterminate color="#00A63E" size="36" />
            <div class="mt-3 loading-text">Loading users...</div>
          </div>
        </template>

        <!-- Name -->
        <template v-slot:[`item.userdetail.name`]="{ item }">
          <div class="d-flex align-center" style="gap: 10px; padding: 6px 0">
            <div class="user-avatar">
              <span>{{ getInitials(item.userdetail?.name) }}</span>
            </div>
            <div class="user-name">{{ item.userdetail?.name }}</div>
          </div>
        </template>

        <!-- UserType -->
        <template v-slot:[`item.useTypeDesc`]="{ item }">
          <span class="usertype-text">{{ item.useTypeDesc }}</span>
        </template>

        <!-- Role chips -->
        <template v-slot:[`item.role`]="{ item }">
          <div class="d-flex flex-wrap ga-1 py-1">
            <v-chip
              v-for="i in item.roles"
              :key="i.id"
              size="x-small"
              class="role-chip"
              variant="tonal"
              color="#00A63E"
            >
              {{ i.description }}
            </v-chip>
          </div>
        </template>

        <!-- Program chips -->
        <template v-slot:[`item.program`]="{ item }">
          <div class="d-flex flex-wrap ga-1 py-1">
            <template v-if="item.usertypeID < 8">
              <v-chip
                v-for="i in item.program"
                :key="i.id"
                size="x-small"
                class="program-chip"
                variant="tonal"
                color="#00A63E"
              >
                {{ i.name }}
              </v-chip>
            </template>
            <template v-else>
              <v-chip
                size="x-small"
                class="program-chip"
                variant="tonal"
                color="#00A63E"
              >
                {{ item.userdetail?.program_name }}
              </v-chip>
            </template>
          </div>
        </template>

        <!-- Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex ga-2 justify-center">
            <v-btn
              size="small"
              class="edit-btn"
              variant="tonal"
              prepend-icon="mdi-pencil-outline"
              rounded="lg"
              @click="editItem(item)"
            >
              Edit
            </v-btn>
            <v-btn
              size="small"
              class="reset-btn"
              variant="tonal"
              prepend-icon="mdi-lock-reset"
              rounded="lg"
              @click="resetPass(item)"
            >
              Reset
            </v-btn>
          </div>
        </template>

        <!-- No data -->
        <template #no-data>
          <div class="no-data-slot">
            <div class="no-data-icon-wrap">
              <v-icon size="36" color="#d1d5db"
                >mdi-account-search-outline</v-icon
              >
            </div>
            <div class="no-data-title">No users found</div>
            <div class="no-data-sub">
              Try adjusting the filter or add a new user.
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialogs -->
    <UserManagementDialog :data="updateData" :action="action" />
    <ResetPasswordDialog :data="resetData" :action="action" />

    <v-dialog v-model="dialogConfirmDelete" max-width="460">
      <v-card class="rounded-2xl pa-2">
        <v-card-title class="d-flex align-center pt-4 px-4" style="gap: 10px">
          <div class="delete-icon-wrap">
            <v-icon color="#ef4444" size="20">mdi-alert-outline</v-icon>
          </div>
          <span style="font-size: 16px; font-weight: 700; color: #111827"
            >Confirm Deletion</span
          >
        </v-card-title>
        <v-card-text class="px-4 py-3" style="font-size: 14px; color: #6b7280">
          Are you sure you want to delete this user? This action cannot be
          undone.
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn
            variant="text"
            class="cancel-btn"
            rounded="lg"
            @click="dialogConfirmDelete = false"
          >
            Cancel
          </v-btn>
          <v-btn class="confirm-delete-btn" rounded="lg" @click="confirmDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <fade-away-message-component
      :displayType="'variation2'"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </div>
</template>

<script>
import eventBus from "@/eventBus";
import UserManagementDialog from "../Dialogs/Forms/UserManagementDialog.vue";
import ResetPasswordDialog from "../Dialogs/Forms/ResetPasswordDialog.vue";

export default {
  components: {
    UserManagementDialog,
    ResetPasswordDialog,
  },

  data: () => ({
    search: "",
    headers: [
      { title: "Name", value: "userdetail.name", align: "start" },
      { title: "User Type", value: "useTypeDesc", align: "start" },
      { title: "Role", value: "role", align: "start" },
      { title: "Program", value: "program", align: "start" },
      {
        title: "Actions",
        value: "actions",
        align: "center",
        sortable: false,
        width: 200,
      },
    ],
    data: [],
    perPageChoices: [
      { text: "5", value: 5 },
      { text: "10", value: 10 },
      { text: "20", value: 20 },
      { text: "50", value: 50 },
      { text: "100", value: 100 },
      { text: "250", value: 250 },
      { text: "500", value: 500 },
    ],
    totalCount: 0,
    deleteData: null,
    updateData: null,
    alerted: false,
    loading: false,
    options: {},
    action: null,
    paginationData: {},
    formdata: [],
    dialogConfirmDelete: false,
    type: null,
    resetData: null,
    usertypeList: [],
    fadeAwayMessage: {
      show: false,
      type: "success",
      header: "Successfully Deleted!",
      message: "",
      top: 10,
    },
  }),

  computed: {
    getTableData() {
      if (this.type == -1) return this.data;
      return this.data.filter((d) => d.usertypeID == this.type);
    },
  },

  mounted() {
    this.getAllUserTypes();
    this.initialize();
    eventBus.on("closeUserManagementDialog", () => {
      this.initialize();
    });
  },

  beforeUnmount() {
    eventBus.off("closeUserManagementDialog");
  },

  methods: {
    pagination(data) {
      this.paginationData = data;
    },

    initialize() {
      this.loading = true;
      this.axiosCall("/user-details/getAllUsers", "GET").then((res) => {
        if (res) {
          this.data = res.data;
          this.loading = false;
        }
      });
    },

    getInitials(name) {
      if (!name) return "?";
      const parts = name.trim().split(" ");
      if (parts.length === 1) return parts[0][0].toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },

    filterTable() {},

    addnew() {
      this.updateData = [{ id: null }];
      this.action = "Add";
    },

    resetPass(item) {
      this.resetData = { email: item.email };
    },

    editItem(item) {
      this.updateData = item;
      this.action = "Update";
    },

    getAllUserTypes() {
      this.axiosCall("/user-type/getAllUsertype", "GET").then((res) => {
        this.type = -1;
        this.usertypeList = res.data;
        this.usertypeList.push({ id: -1, description: "All" });
      });
    },

    confirmDelete() {
      this.axiosCall("/request-type/" + this.deleteData.id, "DELETE").then(
        () => {
          this.fadeAwayMessage.show = true;
          this.itemData = null;
          this.initialize();
        },
      );
    },
  },
};
</script>

<style scoped>
.page-wrapper {
  padding: 4px;
  font-family: "DM Sans", "Nunito", sans-serif;
}

/* ─── Header ─── */
.page-header {
  position: relative;
  padding-left: 18px;
}
.header-accent {
  position: absolute;
  left: 0;
  top: 4px;
  bottom: 4px;
  width: 4px;
  border-radius: 4px;
  background: linear-gradient(180deg, #00a63e 0%, #00c853 100%);
}
.header-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.page-title {
  font-size: 26px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
  line-height: 1.1;
}
.page-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 3px;
  font-weight: 400;
}

/* ─── Add button ─── */
.add-btn {
  background: #00a63e !important;
  color: #fff !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  letter-spacing: 0.1px;
  height: 38px !important;
  box-shadow: 0 2px 8px rgba(0, 166, 62, 0.25) !important;
}
.add-btn:hover {
  background: #008f34 !important;
}

/* ─── Filter card ─── */
.filter-card {
  border: 1px solid #e5e7eb !important;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.05) !important;
  background: #fff !important;
}

.filter-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.filter-divider {
  width: 1px;
  height: 20px;
  background: #e5e7eb;
}
.filter-field-label {
  font-size: 13px;
  color: #374151;
  font-weight: 600;
  white-space: nowrap;
}

/* ─── Table card ─── */
.table-card {
  border: 1px solid #e5e7eb !important;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.05) !important;
  background: #fff !important;
  overflow: hidden;
}
.table-topbar {
  background: #fafafa;
}
.table-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}
.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
  font-size: 11px;
  font-weight: 700;
}

/* ─── User avatar & name ─── */
.user-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border: 1.5px solid #bbf7d0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #065f46;
  text-transform: uppercase;
  flex-shrink: 0;
}
.user-name {
  font-size: 13.5px;
  font-weight: 600;
  color: #111827;
}
.usertype-text {
  font-size: 13px;
  color: #374151;
}

/* ─── Chips ─── */
.role-chip,
.program-chip {
  font-weight: 700 !important;
  font-size: 11px !important;
  letter-spacing: 0.3px;
}

/* ─── Action buttons ─── */
.edit-btn {
  background: #f0fdf4 !important;
  color: #00a63e !important;
  border: 1.5px solid #bbf7d0 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
}
.edit-btn:hover {
  background: #dcfce7 !important;
  border-color: #86efac !important;
}
.reset-btn {
  background: #fffbeb !important;
  color: #d97706 !important;
  border: 1.5px solid #fde68a !important;
  font-weight: 600 !important;
  font-size: 12px !important;
}
.reset-btn:hover {
  background: #fef3c7 !important;
  border-color: #fcd34d !important;
}

/* ─── Delete dialog ─── */
.delete-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cancel-btn {
  font-weight: 600 !important;
  color: #6b7280 !important;
  font-size: 13px !important;
}
.confirm-delete-btn {
  background: #ef4444 !important;
  color: #fff !important;
  font-weight: 600 !important;
  font-size: 13px !important;
}

/* ─── No data ─── */
.no-data-slot {
  padding: 56px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.no-data-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}
.no-data-title {
  font-size: 15px;
  font-weight: 700;
  color: #374151;
}
.no-data-sub {
  font-size: 13px;
  color: #9ca3af;
}
.loading-text {
  font-size: 13px;
  color: #9ca3af;
}

/* ─── Search ─── */
.custom-search :deep(.v-field) {
  border-radius: 10px !important;
  font-size: 13px;
}

/* ─── Table styling ─── */
.users-table :deep(tbody tr:hover td) {
  background: #f9fafb !important;
}
.users-table :deep(th) {
  font-size: 12px !important;
  font-weight: 700 !important;
  color: #6b7280 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 1px solid #f3f4f6 !important;
  background: #fff !important;
}
.users-table :deep(td) {
  border-bottom: 1px solid #f9fafb !important;
  font-size: 13.5px;
}
</style>
